import { useState, useEffect, useImperativeHandle, forwardRef, useRef } from 'react';
import { Icon, Checkbox } from '@repo/ui';
import { cn, dateUtil } from '@xtnd/web/shared/utils';
import { fileUtil } from '@xtnd/web/shared';
import { MatterInvoiceSummaryFile, FileCategory } from '../../types';
import { usePreviewFileQuery } from '../../queries';

interface FileAttachmentsProps {
  files: MatterInvoiceSummaryFile[];
  showCheckbox?: boolean;
  onSelectedFilesChange?: (files: MatterInvoiceSummaryFile[]) => void;
  className?: string;
  columns?: 1 | 2 | 3 | 4;
  showDelete?: boolean;
  onDelete?: (file: MatterInvoiceSummaryFile) => void;
}

export interface FileAttachmentsRef {
  getSelectedFiles: () => MatterInvoiceSummaryFile[];
  clearSelection: () => void;
}

interface FileTypeConfig {
  icon: string;
  label: string;
}

/**
 * Get file extension from file name
 */
const getFileExtension = (fileName: string): string => {
  const parts = fileName.split('.');
  if (parts.length > 1) {
    const extension = parts[parts.length - 1];
    return extension ? extension.toLowerCase() : '';
  }
  return '';
};

/**
 * Get file type configuration based on file extension
 */
const getFileTypeConfig = (fileName: string): FileTypeConfig => {
  const extension = getFileExtension(fileName);

  const fileTypeMap: Record<string, FileTypeConfig> = {
    // PDF files
    pdf: {
      icon: 'HiDocumentText',
      label: 'PDF',
    },
    // Word documents
    doc: {
      icon: 'HiDocumentText',
      label: 'DOC',
    },
    docx: {
      icon: 'HiDocumentText',
      label: 'DOCX',
    },
    // Excel files
    xls: {
      icon: 'HiDocumentText',
      label: 'XLS',
    },
    xlsx: {
      icon: 'HiDocumentText',
      label: 'XLSX',
    },
    // Image files
    jpg: {
      icon: 'HiPhoto',
      label: 'JPG',
    },
    jpeg: {
      icon: 'HiPhoto',
      label: 'JPEG',
    },
    png: {
      icon: 'HiPhoto',
      label: 'PNG',
    },
    gif: {
      icon: 'HiPhoto',
      label: 'GIF',
    },
    // Text files
    txt: {
      icon: 'HiDocumentText',
      label: 'TXT',
    },
    // Email message files
    eml: {
      icon: 'HiMail',
      label: 'EML',
    },
    // Archive files
    zip: {
      icon: 'HiArchive',
      label: 'ZIP',
    },
    rar: {
      icon: 'HiArchive',
      label: 'RAR',
    },
  };

  // Default configuration for unknown file types
  const defaultConfig: FileTypeConfig = {
    icon: 'HiDocumentText',
    label: extension.toUpperCase() || 'FILE',
  };

  return fileTypeMap[extension] || defaultConfig;
};

/**
 * Format date for display
 */
const formatDate = (dateString: string) => {
  return dateUtil.formatDate(dateString, dateUtil.DateFormat.US_WITH_TIME);
};

/**
 * Get grid columns class based on number of columns
 */
const getGridColumnsClass = (columns: 1 | 2 | 3 | 4 = 2): string => {
  const columnMap: Record<1 | 2 | 3 | 4, string> = {
    1: 'grid-cols-1',
    2: 'grid-cols-1 md:grid-cols-2',
    3: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3',
    4: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4',
  };
  return columnMap[columns];
};

/**
 * FileAttachments component - Displays file attachments with optional checkbox selection
 *
 * @param files - Array of file attachments to display
 * @param showCheckbox - Whether to show checkboxes for file selection (default: true)
 * @param onSelectedFilesChange - Callback when selected files change
 * @param className - Additional CSS classes
 * @param columns - Number of columns to display (1, 2, 3, or 4) (default: 2)
 * @param showDelete - Whether to show delete button for each file (default: false)
 * @param onDelete - Callback when delete button is clicked
 * @param ref - Ref to access component methods (getSelectedFiles, clearSelection)
 */
export const FileAttachments = forwardRef<FileAttachmentsRef, FileAttachmentsProps>(
  (
    {
      files,
      showCheckbox = true,
      onSelectedFilesChange,
      className,
      columns = 2,
      showDelete = false,
      onDelete,
    },
    ref
  ) => {
    const [selectedFileIds, setSelectedFileIds] = useState<Set<string>>(new Set());
    const [selectedFiles, setSelectedFiles] = useState<MatterInvoiceSummaryFile | null>(null);
    const prevSelectedFileIdsRef = useRef<string>('');
    const isFirstRenderRef = useRef(true);
    const onSelectedFilesChangeRef = useRef(onSelectedFilesChange);

    // Keep callback ref up to date
    useEffect(() => {
      onSelectedFilesChangeRef.current = onSelectedFilesChange;
    }, [onSelectedFilesChange]);

    const { isLoading: isDocLoading, data: document } = usePreviewFileQuery(
      selectedFiles?.fileId || '',
      { enabled: !!selectedFiles?.fileId }
    );

    // Update parent when selection changes (only when selection actually changes)
    useEffect(() => {
      if (!onSelectedFilesChangeRef.current) {
        return;
      }

      // Skip on first render
      if (isFirstRenderRef.current) {
        isFirstRenderRef.current = false;
        prevSelectedFileIdsRef.current = Array.from(selectedFileIds).sort().join(',');
        return;
      }

      // Create a sorted string representation of selected IDs for comparison
      const currentSelectedIds = Array.from(selectedFileIds).sort().join(',');
      const prevSelectedIds = prevSelectedFileIdsRef.current;

      // Only call callback if selection actually changed
      if (currentSelectedIds !== prevSelectedIds) {
        const selected = files.filter(file => selectedFileIds.has(file.fileId));
        onSelectedFilesChangeRef.current(selected);
        prevSelectedFileIdsRef.current = currentSelectedIds;
      }
    }, [selectedFileIds, files]);

    // Handle file preview/download
    useEffect(() => {
      if (document?.data) {
        window.open(document.data, '_blank');
        setSelectedFiles(null);
      }
    }, [document]);

    // Expose methods via ref
    useImperativeHandle(ref, () => ({
      getSelectedFiles: () => {
        return files.filter(file => selectedFileIds.has(file.fileId));
      },
      clearSelection: () => {
        setSelectedFileIds(new Set());
      },
    }));

    const handleFileClick = (file: MatterInvoiceSummaryFile) => {
      setSelectedFiles(file);
    };

    const handleCheckboxChange = (fileId: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
      const checked = e.target.checked;
      setSelectedFileIds(prev => {
        const newSet = new Set(prev);
        if (checked) {
          newSet.add(fileId);
        } else {
          newSet.delete(fileId);
        }
        return newSet;
      });
    };

    const handleToggleAll = (e: React.ChangeEvent<HTMLInputElement>) => {
      const checked = e.target.checked;
      if (checked) {
        setSelectedFileIds(new Set(files.map(file => file.fileId)));
      } else {
        setSelectedFileIds(new Set());
      }
    };

    const allSelected = files.length > 0 && selectedFileIds.size === files.length;

    if (!files || files.length === 0) {
      return (
        <div
          className={cn(
            'border border-dashed border-xtnd-light dark:border-xtnd-dark-600 rounded-lg p-12 flex flex-col items-center justify-center',
            className
          )}
        >
          <div className="flex items-center justify-center w-16 h-16 bg-xtnd-light dark:bg-xtnd-dark-700 rounded-full mb-4">
            <Icon
              name="HiDocumentText"
              size="lg"
              className="text-xtnd-dark-400 dark:text-xtnd-light-400"
            />
          </div>
          <p className="text-base font-semibold text-xtnd-dark dark:text-xtnd-light mb-1">
            No Files Available
          </p>
          <p className="text-sm text-xtnd-dark-400 dark:text-xtnd-light-400 text-center">
            There are no file attachments to display.
          </p>
        </div>
      );
    }

    return (
      <div className={cn('space-y-4', className)}>
        {/* Header with select all checkbox (only if showCheckbox is true) */}
        {showCheckbox && files.length > 0 && (
          <div className="flex items-center justify-between pb-2 border-b border-xtnd-light dark:border-xtnd-dark-600">
            <div className="flex items-center gap-2">
              <Checkbox
                checked={allSelected}
                onChange={handleToggleAll}
                label={`Select All (${selectedFileIds.size}/${files.length})`}
              />
            </div>
          </div>
        )}

        {/* Files Grid */}
        <div className={cn('grid gap-4', getGridColumnsClass(columns))}>
          {files.map(file => {
            const fileConfig = getFileTypeConfig(file.fileName);
            const isChecked = selectedFileIds.has(file.fileId);
            const isFileLoading = isDocLoading && selectedFiles?.fileId === file.fileId;

            return (
              <div
                key={file.fileId}
                className={cn(
                  'group relative bg-xtnd-white dark:bg-xtnd-dark-800 border border-xtnd-light dark:border-xtnd-dark-600 rounded-xl p-3 flex items-center gap-4 transition-all hover:border-xtnd-blue dark:hover:border-xtnd-blue-600 hover:bg-xtnd-light dark:hover:bg-xtnd-dark-700',
                  isFileLoading &&
                    'cursor-wait opacity-75 border-xtnd-blue dark:border-xtnd-blue-600 bg-xtnd-light dark:bg-xtnd-dark-700',
                  isChecked &&
                    showCheckbox &&
                    'border-xtnd-blue dark:border-xtnd-blue-600 bg-xtnd-light dark:bg-xtnd-dark-700'
                )}
              >
                {/* Checkbox (if enabled) */}
                {showCheckbox && (
                  <div className="shrink-0">
                    <Checkbox
                      checked={isChecked}
                      onChange={handleCheckboxChange(file.fileId)}
                      aria-label={`Select ${file.fileName}`}
                    />
                  </div>
                )}

                {/* File Icon */}
                <div
                  className={cn(
                    'w-10 h-10 rounded-lg flex items-center justify-center shrink-0 bg-xtnd-light dark:bg-xtnd-dark'
                  )}
                >
                  <Icon
                    name={fileConfig.icon as any}
                    size="lg"
                    className="text-xtnd-blue dark:text-xtnd-blue-400"
                  />
                </div>

                {/* File Info */}
                <div className="flex-1 min-w-0">
                  <button
                    type="button"
                    onClick={() => handleFileClick(file)}
                    className="text-sm font-bold text-xtnd-dark dark:text-xtnd-light truncate pr-6 hover:underline hover:text-xtnd-blue dark:hover:text-xtnd-blue-400 text-left w-full"
                    title={file.fileName}
                  >
                    {file.fileName}
                  </button>
                  <div className="flex items-center gap-3 mt-1">
                    <span
                      className={cn(
                        'text-[10px] px-1.5 py-0.5 rounded font-medium uppercase',
                        'bg-xtnd-light dark:bg-xtnd-dark-700',
                        'text-xtnd-dark-400 dark:text-xtnd-light-400'
                      )}
                    >
                      {fileConfig.label}
                    </span>
                    <span className="text-xs text-xtnd-dark-400 dark:text-xtnd-light-400">
                      {fileUtil.formatFileSize(file.size)}
                    </span>
                  </div>
                  <p className="text-[10px] text-xtnd-dark-400 dark:text-xtnd-light-400 mt-2 flex items-center gap-1 italic">
                    <Icon name="HiClock" size="xs" />
                    {formatDate(file.createdAt)}
                  </p>
                </div>

                {/* Action Buttons */}
                <div className="flex items-center gap-2 shrink-0">
                  {/* Download Button */}
                  <button
                    onClick={() => handleFileClick(file)}
                    disabled={isFileLoading}
                    className={cn(
                      'flex items-center gap-1.5 pl-2 pr-3 py-1.5 text-xtnd-dark-400 dark:text-xtnd-light-400 hover:text-xtnd-blue dark:hover:text-xtnd-blue-400 bg-xtnd-white hover:bg-xtnd-dark-800 rounded-lg transition-all shadow-sm border border-transparent hover:border-xtnd-light dark:hover:border-xtnd-dark-600',
                      isFileLoading && 'cursor-wait opacity-75 bg-xtnd-white dark:bg-xtnd-dark-800'
                    )}
                    title="Download file"
                  >
                    {isFileLoading ? (
                      <Icon
                        name="LoadingSpinner"
                        size="md"
                        className="text-gray-400 dark:text-gray-500 group-hover:text-xtnd-blue dark:group-hover:text-xtnd-blue-400 flex-shrink-0"
                      />
                    ) : (
                      <Icon
                        name="HiDownload"
                        size="md"
                        className="text-xtnd-dark-500 dark:text-xtnd-light-500 group-hover:text-xtnd-blue dark:group-hover:text-xtnd-blue-400 flex-shrink-0"
                      />
                    )}
                    <span className="text-xs font-semibold">Download</span>
                  </button>

                  {/* Delete Button */}
                  {showDelete && onDelete && (
                    <button
                      onClick={e => {
                        e.stopPropagation();
                        onDelete(file);
                      }}
                      className={cn(
                        'p-2 text-xtnd-dark-400 dark:text-xtnd-light-400 hover:text-red-500 dark:hover:text-red-400 hover:bg-xtnd-white dark:hover:bg-xtnd-dark-800 rounded-full transition-all shadow-sm border border-transparent hover:border-red-200 dark:hover:border-red-900/50'
                      )}
                      title="Delete file"
                    >
                      <Icon
                        name="HiTrash"
                        size="md"
                        className="text-gray-400 dark:text-gray-500 group-hover:text-red-500 dark:group-hover:text-red-400 flex-shrink-0"
                      />
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }
);

FileAttachments.displayName = 'FileAttachments';

/**
 * Props for ExistingBARFilesSection component
 */
interface ExistingBARFilesSectionProps {
  existingFiles: MatterInvoiceSummaryFile[];
  onSelectedFilesChange?: (files: MatterInvoiceSummaryFile[]) => void;
  columns?: 1 | 2 | 3 | 4;
}

/**
 * ExistingBARFilesSection - Displays existing BAR files grouped by Payment Support and Appeals Support categories
 * Used in both AppealForm and NoAppealForm for consistent file selection UI
 */
export const ExistingBARFilesSection: React.FC<ExistingBARFilesSectionProps> = ({
  existingFiles,
  onSelectedFilesChange,
  columns = 1,
}) => {
  if (!existingFiles || existingFiles.length === 0) {
    return null;
  }

  const paymentSupportFiles = existingFiles.filter(
    file => file.category === FileCategory.PAYMENT_SUPPORT
  );
  const appealsSupportFiles = existingFiles.filter(
    file => file.category === FileCategory.APPEALS_SUPPORT
  );

  return (
    <div className="bg-gray-100 dark:bg-xtnd-dark-700 rounded-lg p-4">
      {paymentSupportFiles.length > 0 && (
        <div className="space-y-2 w-full mb-4">
          <label className="block text-sm font-semibold text-xtnd-dark dark:text-xtnd-light">
            Payment Support Files
          </label>
          <div className="w-full">
            <FileAttachments
              files={paymentSupportFiles}
              showCheckbox
              onSelectedFilesChange={onSelectedFilesChange}
              columns={columns}
            />
          </div>
        </div>
      )}
      {appealsSupportFiles.length > 0 && (
        <div className="space-y-2 w-full">
          <label className="block text-sm font-semibold text-xtnd-dark dark:text-xtnd-light">
            Appeals Support Files
          </label>
          <div className="w-full">
            <FileAttachments
              files={appealsSupportFiles}
              showCheckbox
              onSelectedFilesChange={onSelectedFilesChange}
              columns={columns}
            />
          </div>
        </div>
      )}
    </div>
  );
};
